import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild, inject } from "@angular/core";
import { AbstractControl, FormGroup } from "@angular/forms";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

import { LgdFormControlFactory } from "@tylertech/lgd-reactive-forms";
import { LoggingService } from "@tylertech/core-module";
import { ICustomViewComponent } from "@tylertech/custom-view";
import { ChartBindingObject, ChartDialogService, ChartManagerService, ChartRegistryService } from "@tylertech/charting-module";
import
{
    AccountDetailViewModel,
    ExecutionResult,
    IChartCriteria,
    IGroupOption,
    ProfileSettingViewModel,
    SettingLookupOption,
} from "@tylertech/tyler-api-types";

import { BudgetService, SegmentService } from "../../services";
import { HttpErrorResponse } from "@angular/common/http";

const defaultSelectedValue = "View All";

@Component({
    selector: "general-ledger-next-year-budget-recap-chart",
    templateUrl: "./nextYearBudgetRecapChart.tmpl.html",
    standalone: false
})
export class NextYearBudgetRecapChartComponent implements OnInit, OnDestroy, ICustomViewComponent<ChartBindingObject>
{
    private readonly $loggingService = inject(LoggingService);
    private readonly $chartDialogService = inject(ChartDialogService);
    private readonly $chartRegistryService = inject(ChartRegistryService);
    private readonly $accountBudgetService = inject(BudgetService);
    private readonly $segmentService = inject(SegmentService);

    private readonly destructor$ = new Subject<void>();
    private readonly groupsSubject = new BehaviorSubject<Array<string>>([defaultSelectedValue]);
    readonly customDataBinding: ChartBindingObject;

    @Input("chartId")
    private readonly _chartId: string;

    readonly formGroup: FormGroup;

    @ViewChild("chart")
    readonly chartCanvas: ElementRef<HTMLCanvasElement>;

    private _chartCriteria: IChartCriteria;
    private _groupBy: string;

    ChartData: Array<AccountDetailViewModel>;
    ChartSubtitle: string;

    isLoaded: boolean;

    constructor()
    {
        const $chartManagerService = inject(ChartManagerService);

        $chartManagerService.refreshChartObservable
            .pipe(takeUntil(this.destructor$))
            .subscribe((chartId: string) =>
            {
                if (chartId !== this.chartId)
                {
                    return;
                }
                this.loadChartData();
            });
        this.formGroup = new FormGroup({
            groupBy: LgdFormControlFactory.select()
        });
    }

    get chartId(): string | undefined
    {
        return this._chartId || this.customDataBinding?.chartId;
    }

    @Input()
    get chartCriteria(): IChartCriteria | undefined
    {
        return this._chartCriteria || this.customDataBinding?.chartCriteria;
    }

    set chartCriteria(value: IChartCriteria) { this._chartCriteria = value; }

    private get groupByControl(): AbstractControl<string> { return this.formGroup.get("groupBy"); }

    get groups(): Observable<Array<string>> { return this.groupsSubject.asObservable(); }

    get showChart(): boolean
    {
        return !!this.chartCriteria?.ErrorMessage || this.hasData();
    }

    get chartHasData(): boolean
    {
        return this.hasData();
    }

    get hasGroups(): boolean { return !!this.groupsSubject.value; }

    get errorMessage(): string | undefined
    {
        return this.chartCriteria?.ErrorMessage;
    }

    get ChartTitle(): string
    {
        return `Next Year/Over Period Fiscal Budget Recap`;
    }

    get FiscalName(): string
    {
        // This chart always displays next fiscal year budget during over periods
        return "Next Fiscal Year";
    }

    get groupBy(): ProfileSettingViewModel
    {
        return this.chartCriteria?.Parameters?.find(x => x.Id === "NextYearBudgetRecapByDepartmentGroupById");
    }

    get groupById(): string { return this.groupBySelected?.Value || ""; }
    get groupByDisplay(): string { return this.groupBySelected?.Display || ""; }

    get groupBySelected(): SettingLookupOption | undefined
    {
        const groupBy = this.groupBy;
        if (!groupBy)
        {
            return undefined;
        }

        let value = groupBy.Options.find(x => x.Value === groupBy.Value);
        if (!value)
        {
            value = groupBy.Options.find(x => x.Display === groupBy.Value);
        }
        return value;
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        const self = this;

        this.fetchSegmentValues();

        this._groupBy = this.groupById;

        subscribeToValueChanges(this.groupByControl, (_newValue: string) =>
        {
            this.setFilterValue();

            this.loadChartData();
        });

        new Promise(
            (_resolveFn, rejectFn) =>
            {
                if (!this.hasData())
                {
                    return this.loadChartData();
                }
                try
                {
                    this.updateChartConfiguration();
                }
                catch (err)
                {
                    rejectFn(err);
                }
            })
            .catch(e =>
            {
                console.error("Error on load", e);
            })
            .finally(() =>
            {
                this.isLoaded = true;
            });

        function subscribeToValueChanges<T = unknown>(control: AbstractControl<T>, subscriptionFn: (newValue: T) => void): void
        {
            control.valueChanges.pipe(takeUntil(self.destructor$))
                .subscribe(x =>
                {
                    subscriptionFn(x);
                });
        }
    }

    private fetchSegmentValues(): void
    {
        this.$segmentService.GetSubSegmentValues(this.groupById)
            .then((result: Array<IGroupOption>) =>
            {
                const groups = ["View All"].concat(result.map(x => x.Name));
                this.groupsSubject.next(groups);

                if (groups.length === 1)
                {
                    this.groupByControl.setValue(groups[0]);
                }
            });
    }

    hasData(): boolean
    {
        return this.chartCriteria?.ChartDatas?.length > 0
            && this.chartCriteria.ChartDatas[0].Data.length > 0
            && this.chartCriteria.ChartDatas[0].Data[0].length > 0;
    }

    private loadChartData(): Promise<void>
    {
        this.isLoaded = false;
        return this.$chartRegistryService.GetChartData(this.chartCriteria)
            .then(
                (result: IChartCriteria) =>
                {
                    this.chartCriteria = result;
                },
                (error: HttpErrorResponse) =>
                {
                    this.$loggingService.error("Could not retrieve chart data", error);
                    this.chartCriteria = {
                        ErrorMessage: error.error.Message,
                    } as IChartCriteria;
                }
            )
            .then(
                () =>
                {
                    if (!this.hasData())
                    {
                        return;
                    }
                    try
                    {
                        this.updateChartConfiguration();
                    }
                    catch (err)
                    {
                        return Promise.reject(err);
                    }
                },
                (err: unknown) =>
                {
                    console.error(err);
                }
            )
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    private onChartParametersChanged(): void
    {
        if (this.groupById === this._groupBy)
        {
            return;
        }

        this._groupBy = this.groupById;
        this.groupByControl.setValue(defaultSelectedValue);
        this.fetchSegmentValues();
        this.setFilterValue();
    }

    openSettings(): void
    {
        this.$chartDialogService
            .showChartParametersDialog(this.ChartTitle, this.chartCriteria)
            .then((criteria: IChartCriteria) =>
            {
                this.onChartParametersChanged();
                this.chartCriteria = criteria;
                this.loadChartData();
            });
    }

    private setFilterValue(): void
    {
        let groupByValue: ProfileSettingViewModel = this.chartCriteria?.Parameters?.find(x => x.Id === "FilterValue");
        if (!groupByValue)
        {
            groupByValue = { Id: "FilterValue" } as ProfileSettingViewModel;

            this.chartCriteria?.Parameters?.push(groupByValue);
        }

        groupByValue.Value = this.groupByControl.value !== defaultSelectedValue
            ? this.groupByControl.value
            : undefined;
    }

    private updateChartConfiguration(): void
    {
        this.ChartData = this.chartCriteria.ChartDatas[0].Data[0];
    }
}
